/**
 * Board Store
 *
 * Zustand store for managing board state including groups, columns, items,
 * and their associated values. Provides optimistic updates.
 */

import { create } from 'zustand';
import type { Board, Group, Column, Item } from '../components/board/demo-data';

// Types for the store
interface BoardState {
  // Data
  board: Board | null;
  isLoading: boolean;
  error: string | null;

  // Selection state
  selectedItemIds: Set<string>;
  selectedGroupId: string | null;
  activeItemId: string | null;

  // Edit state
  editingItemId: string | null;
  editingGroupId: string | null;
  editingColumnId: string | null;

  // Actions
  setBoard: (board: Board | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Selection actions
  selectItem: (itemId: string, addToSelection?: boolean) => void;
  deselectItem: (itemId: string) => void;
  selectAllItems: (groupId?: string) => void;
  clearSelection: () => void;
  setActiveItem: (itemId: string | null) => void;

  // Item actions
  addItem: (groupId: string, name: string) => void;
  updateItem: (itemId: string, updates: Partial<Item>) => void;
  deleteItem: (itemId: string) => void;
  moveItem: (itemId: string, targetGroupId: string, position: number) => void;
  reorderItem: (itemId: string, overId: string, groupId: string) => void;
  updateItemValue: (itemId: string, columnId: string, value: unknown) => void;

  // Group actions
  addGroup: (name: string, color?: string) => void;
  updateGroup: (groupId: string, updates: Partial<Group>) => void;
  deleteGroup: (groupId: string) => void;
  reorderGroup: (groupId: string, overId: string) => void;
  collapseGroup: (groupId: string, collapsed: boolean) => void;

  // Column actions
  addColumn: (name: string, type: string) => void;
  updateColumn: (columnId: string, updates: Partial<Column>) => void;
  deleteColumn: (columnId: string) => void;
  reorderColumn: (columnId: string, overId: string) => void;

  // Edit state actions
  setEditingItem: (itemId: string | null) => void;
  setEditingGroup: (groupId: string | null) => void;
  setEditingColumn: (columnId: string | null) => void;
}

// Generate unique IDs
const generateId = () => Math.random().toString(36).substring(2, 15);

// Helper to update positions
function updatePositions<T extends { position: number }>(items: T[]): T[] {
  return items.map((item, i) => ({ ...item, position: i }));
}

// Create the store
export const useBoardStore = create<BoardState>()((set) => ({
  // Initial state
  board: null,
  isLoading: false,
  error: null,
  selectedItemIds: new Set(),
  selectedGroupId: null,
  activeItemId: null,
  editingItemId: null,
  editingGroupId: null,
  editingColumnId: null,

  // Data actions
  setBoard: (board) =>
    set({
      board,
      selectedItemIds: new Set(),
      activeItemId: null,
    }),

  setLoading: (isLoading) => set({ isLoading }),

  setError: (error) => set({ error }),

  // Selection actions
  selectItem: (itemId, addToSelection = false) =>
    set((state) => {
      if (addToSelection) {
        const newSelected = new Set(state.selectedItemIds);
        newSelected.add(itemId);
        return { selectedItemIds: newSelected };
      }
      return { selectedItemIds: new Set([itemId]) };
    }),

  deselectItem: (itemId) =>
    set((state) => {
      const newSelected = new Set(state.selectedItemIds);
      newSelected.delete(itemId);
      return { selectedItemIds: newSelected };
    }),

  selectAllItems: (groupId) =>
    set((state) => {
      if (!state.board) return state;

      const items = groupId
        ? state.board.groups.find((g) => g.id === groupId)?.items || []
        : state.board.groups.flatMap((g) => g.items);

      return { selectedItemIds: new Set(items.map((i) => i.id)) };
    }),

  clearSelection: () => set({ selectedItemIds: new Set() }),

  setActiveItem: (activeItemId) => set({ activeItemId }),

  // Item actions
  addItem: (groupId, name) =>
    set((state) => {
      if (!state.board) return state;

      const group = state.board.groups.find((g) => g.id === groupId);
      if (!group) return state;

      const newItem: Item = {
        id: generateId(),
        name,
        position: group.items.length,
        values: {},
      };

      const newGroups = state.board.groups.map((g) =>
        g.id === groupId ? { ...g, items: [...g.items, newItem] } : g
      );

      return { board: { ...state.board, groups: newGroups } };
    }),

  updateItem: (itemId, updates) =>
    set((state) => {
      if (!state.board) return state;

      const newGroups = state.board.groups.map((group) => ({
        ...group,
        items: group.items.map((item) =>
          item.id === itemId ? { ...item, ...updates } : item
        ),
      }));

      return { board: { ...state.board, groups: newGroups } };
    }),

  deleteItem: (itemId) =>
    set((state) => {
      if (!state.board) return state;

      const newGroups = state.board.groups.map((group) => ({
        ...group,
        items: updatePositions(group.items.filter((item) => item.id !== itemId)),
      }));

      const newSelected = new Set(state.selectedItemIds);
      newSelected.delete(itemId);

      return {
        board: { ...state.board, groups: newGroups },
        selectedItemIds: newSelected,
        activeItemId: state.activeItemId === itemId ? null : state.activeItemId,
      };
    }),

  moveItem: (itemId, targetGroupId, position) =>
    set((state) => {
      if (!state.board) return state;

      // Find the item first
      let movedItem: Item | undefined;
      for (const group of state.board.groups) {
        const found = group.items.find((i) => i.id === itemId);
        if (found) {
          movedItem = found;
          break;
        }
      }

      if (!movedItem) return state;

      // Remove from source group
      const groupsAfterRemoval = state.board.groups.map((group) => ({
        ...group,
        items: updatePositions(group.items.filter((i) => i.id !== itemId)),
      }));

      // Insert into target group
      const itemToInsert: Item = { ...movedItem, position };
      const newGroups = groupsAfterRemoval.map((group) => {
        if (group.id !== targetGroupId) return group;

        const newItems = [...group.items];
        newItems.splice(position, 0, itemToInsert);
        return { ...group, items: updatePositions(newItems) };
      });

      return { board: { ...state.board, groups: newGroups } };
    }),

  reorderItem: (itemId, overId, groupId) =>
    set((state) => {
      if (!state.board) return state;

      const newGroups = state.board.groups.map((group) => {
        if (group.id !== groupId) return group;

        const oldIndex = group.items.findIndex((i) => i.id === itemId);
        const newIndex = group.items.findIndex((i) => i.id === overId);
        if (oldIndex === -1 || newIndex === -1) return group;

        const newItems = [...group.items];
        const removed = newItems.splice(oldIndex, 1)[0];
        if (!removed) return group;
        newItems.splice(newIndex, 0, removed);
        return { ...group, items: updatePositions(newItems) };
      });

      return { board: { ...state.board, groups: newGroups } };
    }),

  updateItemValue: (itemId, columnId, value) =>
    set((state) => {
      if (!state.board) return state;

      const newGroups = state.board.groups.map((group) => ({
        ...group,
        items: group.items.map((item) =>
          item.id === itemId
            ? { ...item, values: { ...item.values, [columnId]: value } }
            : item
        ),
      }));

      return { board: { ...state.board, groups: newGroups } };
    }),

  // Group actions
  addGroup: (name, color = '#808080') =>
    set((state) => {
      if (!state.board) return state;

      const newGroup: Group = {
        id: generateId(),
        name,
        color,
        position: state.board.groups.length,
        items: [],
      };

      return {
        board: { ...state.board, groups: [...state.board.groups, newGroup] },
      };
    }),

  updateGroup: (groupId, updates) =>
    set((state) => {
      if (!state.board) return state;

      const newGroups = state.board.groups.map((group) =>
        group.id === groupId ? { ...group, ...updates } : group
      );

      return { board: { ...state.board, groups: newGroups } };
    }),

  deleteGroup: (groupId) =>
    set((state) => {
      if (!state.board) return state;

      const group = state.board.groups.find((g) => g.id === groupId);
      const itemIdsToRemove = group?.items.map((i) => i.id) || [];

      const newGroups = updatePositions(
        state.board.groups.filter((g) => g.id !== groupId)
      );

      const newSelected = new Set(state.selectedItemIds);
      itemIdsToRemove.forEach((id) => newSelected.delete(id));

      return {
        board: { ...state.board, groups: newGroups },
        selectedItemIds: newSelected,
      };
    }),

  reorderGroup: (groupId, overId) =>
    set((state) => {
      if (!state.board) return state;

      const oldIndex = state.board.groups.findIndex((g) => g.id === groupId);
      const newIndex = state.board.groups.findIndex((g) => g.id === overId);
      if (oldIndex === -1 || newIndex === -1) return state;

      const newGroups = [...state.board.groups];
      const removed = newGroups.splice(oldIndex, 1)[0];
      if (!removed) return state;
      newGroups.splice(newIndex, 0, removed);

      return { board: { ...state.board, groups: updatePositions(newGroups) } };
    }),

  collapseGroup: (groupId, collapsed) =>
    set((state) => {
      if (!state.board) return state;

      const newGroups = state.board.groups.map((group) =>
        group.id === groupId ? { ...group, collapsed } : group
      );

      return { board: { ...state.board, groups: newGroups } };
    }),

  // Column actions
  addColumn: (name, type) =>
    set((state) => {
      if (!state.board) return state;

      const newColumn: Column = {
        id: generateId(),
        name,
        type: type as Column['type'],
        width: 140,
        position: state.board.columns.length,
      };

      return {
        board: { ...state.board, columns: [...state.board.columns, newColumn] },
      };
    }),

  updateColumn: (columnId, updates) =>
    set((state) => {
      if (!state.board) return state;

      const newColumns = state.board.columns.map((column) =>
        column.id === columnId ? { ...column, ...updates } : column
      );

      return { board: { ...state.board, columns: newColumns } };
    }),

  deleteColumn: (columnId) =>
    set((state) => {
      if (!state.board) return state;

      const newColumns = updatePositions(
        state.board.columns.filter((c) => c.id !== columnId)
      );

      // Remove column values from all items
      const newGroups = state.board.groups.map((group) => ({
        ...group,
        items: group.items.map((item) => {
          const newValues = { ...item.values };
          delete newValues[columnId];
          return { ...item, values: newValues };
        }),
      }));

      return {
        board: { ...state.board, columns: newColumns, groups: newGroups },
      };
    }),

  reorderColumn: (columnId, overId) =>
    set((state) => {
      if (!state.board) return state;

      const oldIndex = state.board.columns.findIndex((c) => c.id === columnId);
      const newIndex = state.board.columns.findIndex((c) => c.id === overId);
      if (oldIndex === -1 || newIndex === -1) return state;

      const newColumns = [...state.board.columns];
      const removed = newColumns.splice(oldIndex, 1)[0];
      if (!removed) return state;
      newColumns.splice(newIndex, 0, removed);

      return { board: { ...state.board, columns: updatePositions(newColumns) } };
    }),

  // Edit state actions
  setEditingItem: (editingItemId) => set({ editingItemId }),
  setEditingGroup: (editingGroupId) => set({ editingGroupId }),
  setEditingColumn: (editingColumnId) => set({ editingColumnId }),
}));

// Selectors
export const selectBoard = (state: BoardState) => state.board;
export const selectGroups = (state: BoardState) => state.board?.groups ?? [];
export const selectColumns = (state: BoardState) => state.board?.columns ?? [];
export const selectSelectedItemIds = (state: BoardState) => state.selectedItemIds;
export const selectActiveItemId = (state: BoardState) => state.activeItemId;
export const selectIsLoading = (state: BoardState) => state.isLoading;
export const selectError = (state: BoardState) => state.error;

// Computed selectors
export const selectItemById = (itemId: string) => (state: BoardState) => {
  if (!state.board) return null;
  for (const group of state.board.groups) {
    const item = group.items.find((i) => i.id === itemId);
    if (item) return { item, group };
  }
  return null;
};

export const selectGroupById = (groupId: string) => (state: BoardState) => {
  return state.board?.groups.find((g) => g.id === groupId) ?? null;
};

export const selectColumnById = (columnId: string) => (state: BoardState) => {
  return state.board?.columns.find((c) => c.id === columnId) ?? null;
};
